// @ts-nocheck
/*
 * Reset-password page — v1 logic preserved end-to-end (state, validation,
 * API sequence, toasts). UI ported off react-bootstrap onto shadcn so we
 * stop shipping two UI libraries in the Users flow.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { patchApiCallWithHeader, getApiCall } from '@/service/httpCall';
import { API_USER_STATUS_UPDATE } from '@/service/url';
import { ROUTES, BACKEND_API_BASE_URL } from '@/utilities/constant';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { AclDenied } from '@/components/feedback/AclDenied';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { MODULE_ACL } from '@/utilities/constant';
import { PageHeader } from '@/components/feedback/PageHeader';

const UserResetPassword = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useModuleAcl(MODULE_ACL.USERS);

  const [buttonDisable, setButtonDisable] = useState(false);
  const [username, setUsername] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordInputs, setShowPasswordInputs] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(null);

  const [, setShowToast] = useState(false);
  const [, setToastProps] = useState(null);

  useEffect(() => {
    const SELECTED_USER_ROUTE = `${API_USER_STATUS_UPDATE}/${id}`;
    getApiCall(SELECTED_USER_ROUTE, {}).then(getUserDataRecordSuccess).catch(getUserDataError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserDataRecordSuccess = (resp) => {
    if (resp) setUsername(resp.username);
  };

  const handleValidateUsername = (e) => {
    e.preventDefault();
    setShowPasswordInputs(false);
    const ALL_USERS_ROUTE = `${BACKEND_API_BASE_URL}/v2/${ROUTES.USERS.LIST}`;
    if (!username.trim().length) {
      toast.error('Please add a valid username');
      return;
    }
    getApiCall(ALL_USERS_ROUTE, {}).then(getUserDataSuccess).catch(getUserDataError);
  };

  const handleResetPassword = (e) => {
    e.preventDefault();

    if (!password.trim().length) {
      toast.error("Password Can't be blank");
      return;
    }
    if (password === selectedUser) {
      toast.error("New Password Can't be same as Older Password.");
      return;
    }
    if (!confirmPassword.trim().length) {
      toast.error("Password Can't be blank");
      return;
    }

    if (password === confirmPassword) {
      const payload = { newPassword: confirmPassword };
      const SELECTED_USER_ROUTE = `${API_USER_STATUS_UPDATE}/${id}`;
      patchApiCallWithHeader(SELECTED_USER_ROUTE, payload)
        .then(handleUpdateUserPassword)
        .catch(getUserDataError);
    } else {
      toast.error('Password and confirm password are different');
    }
  };

  const getUserDataSuccess = (resp) => {
    if (!resp) return;
    const filteredUserData = resp.filter(
      (user) => user.username?.toLowerCase().trim() === username?.toLowerCase().trim(),
    );
    if (filteredUserData.length < 1) {
      toast.error('User not found. Try some different username.');
      return;
    }
    if (filteredUserData.length === 1) {
      setSelectedUser(filteredUserData[0].actual_password);
      toast.success('User found.');
      setTimeout(() => setShowPasswordInputs(true), 200);
    }
  };

  const handleUpdateUserPassword = () => {
    toast.success('Password Changed');
    setTimeout(() => navigate('/users'), 1000);
  };

  const getUserDataError = (err) => {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
      setShowPasswordInputs(false);
    } else {
      setButtonDisable(false);
      setShowPasswordInputs(false);
      toast.error(friendlyApiError(err) || 'Something went wrong');
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    const errors = [];
    if (value.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(value)) errors.push('At least 1 uppercase letter');
    if (!/[a-z]/.test(value)) errors.push('At least 1 lowercase letter');
    if (!/[0-9]/.test(value)) errors.push('At least 1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) errors.push('At least 1 special character');
    if (/\s/.test(value)) errors.push('No spaces allowed');
    if (value.length === 0) setPasswordError('');
    else if (errors.length > 0) setPasswordError(errors);
    else setPasswordError('');
    if (confirmPassword) setPasswordMatch(value === confirmPassword);
  };

  if (!canEdit) return <AclDenied operation="edit" />;

  return (
    <ScrollPage>
      <PageHeader
        title="Reset Password"
        description="Set a new password for the selected user."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />
      <Card>
        <CardContent className="space-y-6 pt-6">
          <form onSubmit={handleValidateUsername} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium leading-none">
                  User Name
                </label>
                <Input
                  type="text"
                  id="username"
                  required
                  placeholder="User Name"
                  value={username}
                  readOnly
                />
              </div>
            </div>
            <Button type="submit" disabled={buttonDisable} className="gap-2">
              Continue
              {buttonDisable && <Loader2 className="h-4 w-4 animate-spin" />}
            </Button>
          </form>

          {showPasswordInputs ? (
            <form onSubmit={handleResetPassword} className="space-y-4 border-t pt-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium leading-none">
                    Enter new password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      placeholder="Password"
                      required
                      value={password}
                      onChange={handlePasswordChange}
                      className="!pr-11"
                      style={{ paddingRight: '2.75rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {Array.isArray(passwordError) && passwordError.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-destructive">
                      {passwordError.map((err, index) => (
                        <li key={index}>• {err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="confirm_password" className="text-sm font-medium leading-none">
                    Re-enter new password
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirm_password"
                      placeholder="Confirm Password"
                      required
                      value={confirmPassword}
                      onChange={(e) => {
                        const value = e.target.value;
                        setConfirmPassword(value);
                        setPasswordMatch(password === value);
                      }}
                      className="!pr-11"
                      style={{ paddingRight: '2.75rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {confirmPassword && (
                    <p
                      className={
                        passwordMatch
                          ? 'text-xs text-emerald-600 dark:text-emerald-400'
                          : 'text-xs text-destructive'
                      }
                    >
                      {passwordMatch ? '✔ Passwords match' : '✖ Passwords do not match'}
                    </p>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={buttonDisable} className="gap-2">
                Change Password
                {buttonDisable && <Loader2 className="h-4 w-4 animate-spin" />}
              </Button>
            </form>
          ) : null}
        </CardContent>
      </Card>
    </ScrollPage>
  );
};

export default UserResetPassword;
